import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Icon } from "../lib/Icon";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { useToast } from "../lib/toast";
import { ApiError } from "../api/client";
import { counterProposal, createProposal, type TradeItemIn } from "../api/trades";
import { fetchUserDuplicates, fetchUserMissing } from "../api/users";

interface Props {
  toUserId: number;
  toUserName: string;
  /** Pre-fill: stickers que YO le DOY */
  prefilledGive?: number[];
  /** Pre-fill: stickers que YO RECIBO de él */
  prefilledReceive?: number[];
  /** Pre-fill: dinero encima (positivo = YO encimo, negativo = ÉL encima) */
  prefilledCashDelta?: number;
  /** Pre-fill: mensaje */
  prefilledMessage?: string;
  /** Si está, el modal funciona como contraoferta a esa propuesta. */
  counterOfId?: number;
  onClose: () => void;
  onSent: () => void;
}

interface Selected {
  sticker_id: number;
  quantity: number;
}

type PickerFilter = "smart" | "my-dups" | "my-missing" | "all";

export function ProposeTradeModal({
  toUserId,
  toUserName,
  prefilledGive = [],
  prefilledReceive = [],
  prefilledCashDelta = 0,
  prefilledMessage = "",
  counterOfId,
  onClose,
  onSent,
}: Props) {
  const { stickers, stickerById, teamById, teams } = useCatalog();
  const { ownership } = useInventory();
  const toast = useToast();

  // Si ambos prefilled vienen con items (caso típico desde MatchesScreen),
  // equilibramos a la cantidad MÍNIMA de los dos. Modelo 1:1 — si yo solo
  // tengo 10 dups que le sirven, no tiene sentido pedirle 20. El user puede
  // ajustar abriendo el picker después.
  const [give, setGive] = useState<Selected[]>(() => {
    const balanced = prefilledReceive.length > 0 && prefilledGive.length > 0
      ? prefilledGive.slice(0, Math.min(prefilledGive.length, prefilledReceive.length))
      : prefilledGive;
    return balanced.map((id) => ({ sticker_id: id, quantity: 1 }));
  });
  const [receive, setReceive] = useState<Selected[]>(() => {
    const balanced = prefilledReceive.length > 0 && prefilledGive.length > 0
      ? prefilledReceive.slice(0, Math.min(prefilledGive.length, prefilledReceive.length))
      : prefilledReceive;
    return balanced.map((id) => ({ sticker_id: id, quantity: 1 }));
  });
  const [message, setMessage] = useState(prefilledMessage);
  const [submitting, setSubmitting] = useState(false);
  // Quien encima dinero ("none" | "me" | "them") y cuánto (siempre positivo)
  const [cashSide, setCashSide] = useState<"none" | "me" | "them">(
    prefilledCashDelta > 0 ? "me" : prefilledCashDelta < 0 ? "them" : "none",
  );
  const [cashAmount, setCashAmount] = useState<number>(Math.abs(prefilledCashDelta));
  const isCounter = !!counterOfId;

  // Inventario del peer (lo que tiene repetida y lo que le falta)
  const [peerDups, setPeerDups] = useState<Set<number>>(new Set());
  const [peerMissing, setPeerMissing] = useState<Set<number>>(new Set());
  const [peerLoaded, setPeerLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchUserDuplicates(toUserId), fetchUserMissing(toUserId)])
      .then(([dups, miss]) => {
        if (cancelled) return;
        setPeerDups(new Set(dups));
        setPeerMissing(new Set(miss));
        setPeerLoaded(true);
      })
      .catch(() => setPeerLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [toUserId]);

  // Auto-fill UNA SOLA VEZ cuando peer + inventario están listos.
  // - GIVE: TODAS mis dups (count >= 2) que él NECESITA
  // - RECEIVE: misma cantidad que GIVE, de las que él tiene en dup Y yo
  //   necesito (count == 0 o sin entry)
  // Skip si: es counter, ya hay prefilled, o ya se hizo el auto-fill.
  const autoFilledRef = useRef(false);
  useEffect(() => {
    if (autoFilledRef.current) return;
    if (!peerLoaded) return;
    if (isCounter) return;
    if (prefilledGive.length > 0 || prefilledReceive.length > 0) return;
    // Esperar a que el inventario esté cargado (sino daría siempre give=[])
    if (Object.keys(ownership).length === 0) return;

    autoFilledRef.current = true;

    const giveIds: number[] = [];
    for (const [sid, count] of Object.entries(ownership)) {
      const id = Number(sid);
      if (count >= 2 && peerMissing.has(id)) giveIds.push(id);
    }
    const receiveCandidates: number[] = [];
    for (const id of peerDups) {
      const c = ownership[id] || 0;
      if (c === 0) receiveCandidates.push(id);
    }
    const target = giveIds.length;
    const receiveIds = receiveCandidates.slice(0, target);

    setGive(giveIds.map((id) => ({ sticker_id: id, quantity: 1 })));
    setReceive(receiveIds.map((id) => ({ sticker_id: id, quantity: 1 })));
  }, [peerLoaded, ownership, peerMissing, peerDups, isCounter, prefilledGive.length, prefilledReceive.length]);

  // Picker state
  const [pickerSide, setPickerSide] = useState<"give" | "receive" | null>(null);
  const [search, setSearch] = useState("");
  const [pickerFilter, setPickerFilter] = useState<PickerFilter>("smart");
  const [pickerTeamId, setPickerTeamId] = useState<number | "all">("all");
  // Multi-selección: IDs marcados durante la sesión del picker.
  const [pickerSelected, setPickerSelected] = useState<Set<number>>(new Set());
  // Snapshot de los IDs ya en el sidepanel al abrir el picker. Estos van
  // SIEMPRE arriba en el listado, aunque el user los destilde — así no
  // pierde el contexto del intercambio actual mientras explora la lista.
  const [pickerPinned, setPickerPinned] = useState<Set<number>>(new Set());

  // Cuando se abre el picker, pre-seleccionar automáticamente para que el
  // usuario no tenga que hacerlo a mano:
  //
  // - GIVE: TODAS las que YO TENGO EN DUPLICADO (count >= 2) Y ÉL NECESITA.
  //   No incluye las que tengo simple (count == 1) porque dárselas me dejaría
  //   sin esa lámina. Si quiero ofrecer una simple, voy a "Todo el catálogo"
  //   y la marco manualmente.
  //
  // - RECEIVE: HASTA N láminas que ÉL tiene en duplicado Y yo necesito,
  //   donde N = cantidad de láminas que voy a dar. Esto refleja el modelo
  //   típico 1:1 — si doy 10, pido 10. Si give está vacío, uso como
  //   referencia mis matches perfectos en give. Si peer tiene más matches
  //   que yo (caso común — peer con 250 dups), solo se pre-marcan las
  //   primeras N. El usuario puede destildar y elegir manualmente con los
  //   filtros de equipo / búsqueda.
  useEffect(() => {
    if (pickerSide === null) return;
    const current = pickerSide === "give" ? give : receive;
    const initial = new Set(current.map((s) => s.sticker_id));
    // Snapshot de lo que ya está en el sidepanel — para pinear arriba en el
    // listado del picker (que son justamente las que componen el intercambio).
    setPickerPinned(new Set(initial));

    if (peerLoaded) {
      if (pickerSide === "give") {
        // GIVE: si el sidepanel está vacío, completar con TODAS mis dups
        // que él necesita. Si ya tiene items, no agregar nada (ya pre-llenó
        // el modal con auto-fill, no duplicar).
        if (initial.size === 0) {
          for (const id of peerMissing) {
            if (myDupIds.has(id)) initial.add(id);
          }
        }
      } else {
        // RECEIVE: el TOTAL de marcadas debe ser min(give.length, peer.dups).
        // Si el sidepanel ya tiene 10 (=give.length), NO agregamos más
        // (initial.size ya es 10). Si está vacío, completar hasta budget.
        const myMatchPotential = Array.from(peerMissing).filter((id) => myDupIds.has(id)).length;
        const budget = give.length || myMatchPotential;
        for (const id of peerDups) {
          if (initial.size >= budget) break;
          if (!myOwnedIds.has(id) && !initial.has(id)) {
            initial.add(id);
          }
        }
      }
    }
    setPickerSelected(initial);
    setSearch("");
    setPickerTeamId("all");
  }, [pickerSide, peerLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const myOwnedIds = useMemo(() => {
    const set = new Set<number>();
    for (const [id, count] of Object.entries(ownership)) {
      if (count >= 1) set.add(Number(id));
    }
    return set;
  }, [ownership]);

  const myDupIds = useMemo(() => {
    const set = new Set<number>();
    for (const [id, count] of Object.entries(ownership)) {
      if (count >= 2) set.add(Number(id));
    }
    return set;
  }, [ownership]);

  const filteredStickerIds = useMemo(() => {
    let pool = stickers;

    if (pickerFilter === "smart" && pickerSide) {
      if (pickerSide === "give") {
        // Smart = solo MIS REPETIDAS que él necesita. No incluyo las que
        // tengo simple porque dárselas me dejaría sin la lámina.
        pool = pool.filter((s) => peerMissing.has(s.id) && myDupIds.has(s.id));
      } else {
        pool = pool.filter((s) => peerDups.has(s.id));
      }
    } else if (pickerFilter === "my-dups") {
      pool = pool.filter((s) => myDupIds.has(s.id));
    } else if (pickerFilter === "my-missing") {
      pool = pool.filter((s) => !myOwnedIds.has(s.id));
    }

    if (pickerTeamId !== "all") {
      pool = pool.filter((s) => s.team_id === pickerTeamId);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      pool = pool.filter((s) => {
        const t = teamById(s.team_id);
        return `${s.name} ${s.num} ${t?.code ?? ""} ${t?.name ?? ""}`.toLowerCase().includes(q);
      });
    }

    // Sort:
    //  1) PINNED arriba (las que ya estaban en el sidepanel al abrir el
    //     picker — son las del intercambio actual)
    //  2) Match score descendente (perfectos arriba)
    //  3) Por número de catálogo (alfabético)
    const sorted = [...pool].sort((a, b) => {
      const aPinned = pickerPinned.has(a.id) ? 1 : 0;
      const bPinned = pickerPinned.has(b.id) ? 1 : 0;
      if (aPinned !== bPinned) return bPinned - aPinned;
      const aScore = matchScore(a.id, pickerSide, peerMissing, peerDups, myOwnedIds, myDupIds, ownership);
      const bScore = matchScore(b.id, pickerSide, peerMissing, peerDups, myOwnedIds, myDupIds, ownership);
      if (bScore !== aScore) return bScore - aScore;
      return a.num.localeCompare(b.num);
    });
    return sorted.map((s) => s.id);
  }, [stickers, teamById, search, pickerFilter, pickerSide, pickerTeamId, peerMissing, peerDups, myOwnedIds, myDupIds, ownership, pickerPinned]);

  // Multi-selección: toggle del id en pickerSelected.
  const togglePick = (stickerId: number) => {
    setPickerSelected((prev) => {
      const next = new Set(prev);
      if (next.has(stickerId)) next.delete(stickerId);
      else next.add(stickerId);
      return next;
    });
  };

  // Aplicar la selección al lado activo y cerrar el picker.
  // - Mantiene cantidades existentes para items que ya estaban
  // - Quita los items que el usuario deseleccionó
  // - Agrega los nuevos con quantity=1
  const commitPickerSelection = () => {
    if (!pickerSide) return;
    const setter = pickerSide === "give" ? setGive : setReceive;
    const current = pickerSide === "give" ? give : receive;
    const currentMap = new Map(current.map((s) => [s.sticker_id, s.quantity]));
    const next: Selected[] = Array.from(pickerSelected).map((sid) => ({
      sticker_id: sid,
      quantity: currentMap.get(sid) ?? 1,
    }));
    setter(next);
    setPickerSide(null);
    setSearch("");
  };

  const removeFromSide = (side: "give" | "receive", stickerId: number) => {
    if (side === "give") setGive(give.filter((s) => s.sticker_id !== stickerId));
    else setReceive(receive.filter((s) => s.sticker_id !== stickerId));
  };

  const updateQty = (side: "give" | "receive", stickerId: number, qty: number) => {
    if (qty < 1) return;
    const setter = side === "give" ? setGive : setReceive;
    const current = side === "give" ? give : receive;
    setter(current.map((s) => (s.sticker_id === stickerId ? { ...s, quantity: qty } : s)));
  };

  const cashDelta = cashSide === "me" ? cashAmount : cashSide === "them" ? -cashAmount : 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (give.length === 0) {
      toast("Agrega al menos una lámina que vas a dar");
      return;
    }
    if (receive.length === 0) {
      toast("Agrega al menos una lámina que vas a recibir");
      return;
    }
    setSubmitting(true);
    try {
      const items: TradeItemIn[] = [
        ...give.map((s) => ({ sticker_id: s.sticker_id, direction: "give" as const, quantity: s.quantity })),
        ...receive.map((s) => ({ sticker_id: s.sticker_id, direction: "receive" as const, quantity: s.quantity })),
      ];
      if (isCounter && counterOfId) {
        await counterProposal(counterOfId, {
          message: message.trim() || undefined,
          items,
          cash_delta: cashDelta,
        });
        toast(`Contraoferta enviada a ${toUserName}`);
      } else {
        await createProposal({
          to_user_id: toUserId,
          message: message.trim() || undefined,
          items,
          cash_delta: cashDelta,
        });
        toast(`Propuesta enviada a ${toUserName}`);
      }
      onSent();
    } catch (e) {
      toast(`Error: ${e instanceof ApiError ? e.message : e}`);
    } finally {
      setSubmitting(false);
    }
  };

  const smartCount = pickerSide === "give"
    ? Array.from(peerMissing).filter((id) => myDupIds.has(id)).length
    : peerDups.size;

  const myDupsCount = myDupIds.size;
  const myMissingCount = stickers.length - myOwnedIds.size;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 999, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 720, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800, margin: 0 }}>
            {isCounter ? `Contraproponer a ${toUserName}` : `Proponer trato a ${toUserName}`}
          </h2>
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={onClose} aria-label="Cerrar"><Icon name="x" size={16} /></button>
        </div>
        {peerLoaded && (
          <div className="tiny muted" style={{ marginBottom: 14 }}>
            {toUserName} tiene <b>{peerDups.size}</b> repetidas para dar · necesita <b>{peerMissing.size}</b> láminas
          </div>
        )}

        <form onSubmit={submit} className="col" style={{ gap: 16 }}>
          <div className="propose-grid">
            <SidePanel
              title="TÚ DAS"
              accent="var(--primary)"
              items={give}
              onAdd={() => setPickerSide("give")}
              onRemove={(id) => removeFromSide("give", id)}
              onQtyChange={(id, q) => updateQty("give", id, q)}
            />
            <SidePanel
              title="RECIBES"
              accent="var(--accent-amber)"
              items={receive}
              onAdd={() => setPickerSide("receive")}
              onRemove={(id) => removeFromSide("receive", id)}
              onQtyChange={(id, q) => updateQty("receive", id, q)}
            />
          </div>

          <div className="field">
            <label>Encima dinero (opcional)</label>
            <div className="cash-encima-row">
              <button
                type="button"
                className={"cash-side-btn " + (cashSide === "none" ? "active" : "")}
                onClick={() => { setCashSide("none"); setCashAmount(0); }}
              >
                Solo láminas
              </button>
              <button
                type="button"
                className={"cash-side-btn " + (cashSide === "me" ? "active" : "")}
                onClick={() => setCashSide("me")}
              >
                Yo encimo $
              </button>
              <button
                type="button"
                className={"cash-side-btn " + (cashSide === "them" ? "active" : "")}
                onClick={() => setCashSide("them")}
              >
                Que él encime $
              </button>
            </div>
            {cashSide !== "none" && (
              <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>$</span>
                <input
                  type="number"
                  min={0}
                  step={1000}
                  value={cashAmount || ""}
                  onChange={(e) => setCashAmount(Math.max(0, Number(e.target.value) || 0))}
                  placeholder="20000"
                  style={{ flex: 1 }}
                />
                <span className="tiny muted">COP</span>
              </div>
            )}
            <div className="hint">
              {cashSide === "me" && cashAmount > 0
                ? `Tú entregas las láminas + $${cashAmount.toLocaleString("es-CO")}.`
                : cashSide === "them" && cashAmount > 0
                ? `Él entrega las láminas + $${cashAmount.toLocaleString("es-CO")}.`
                : "Solo intercambio de láminas, sin dinero."}
            </div>
          </div>

          <div className="field">
            <label>Mensaje (opcional)</label>
            <textarea
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={isCounter
                ? "Ej: Hola, en lugar de eso prefiero esta combinación, ¿te parece?"
                : "Ej: Hola! Vi que tienes Messi y yo tengo Cristiano. ¿Te interesa?"}
              maxLength={500}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={submitting || give.length === 0 || receive.length === 0}>
              {submitting ? "Enviando…" : (isCounter ? "Enviar contraoferta" : "Enviar propuesta")}
            </button>
          </div>
        </form>

        {pickerSide && (
          <div onClick={() => setPickerSide(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "grid", placeItems: "center", padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg)", borderRadius: 14, maxWidth: 540, width: "100%", maxHeight: "80vh", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontFamily: "var(--font-display)" }}>
                  Agregar lámina ({pickerSide === "give" ? "que TÚ DAS" : "que RECIBES"})
                </h3>
                <div style={{ flex: 1 }} />
                <button className="icon-btn" onClick={() => setPickerSide(null)} aria-label="Cerrar"><Icon name="x" size={16} /></button>
              </div>

              {peerLoaded && (
                <div className="propose-filter-tabs" style={{ flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className={"propose-filter-tab " + (pickerFilter === "smart" ? "active" : "")}
                    onClick={() => setPickerFilter("smart")}
                  >
                    {pickerSide === "give"
                      ? `🎯 Lo que él necesita (${smartCount})`
                      : `🔥 Sus repetidas (${smartCount})`}
                  </button>
                  {pickerSide === "give" ? (
                    <button
                      type="button"
                      className={"propose-filter-tab " + (pickerFilter === "my-dups" ? "active" : "")}
                      onClick={() => setPickerFilter("my-dups")}
                      title="Todas tus repetidas, ordenadas por cantidad"
                    >
                      🔥 Mis repetidas ({myDupsCount})
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={"propose-filter-tab " + (pickerFilter === "my-missing" ? "active" : "")}
                      onClick={() => setPickerFilter("my-missing")}
                      title="Las que te faltan en el álbum"
                    >
                      ❤️ Mis faltantes ({myMissingCount})
                    </button>
                  )}
                  <button
                    type="button"
                    className={"propose-filter-tab " + (pickerFilter === "all" ? "active" : "")}
                    onClick={() => setPickerFilter("all")}
                  >
                    Todo el catálogo ({stickers.length})
                  </button>
                </div>
              )}

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div className="search-box" style={{ flex: 1, minWidth: 180 }}>
                  <Icon name="search" size={16} />
                  <input autoFocus placeholder="Buscar jugador, número, país…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <select
                  value={pickerTeamId}
                  onChange={(e) => setPickerTeamId(e.target.value === "all" ? "all" : Number(e.target.value))}
                  style={{
                    padding: "6px 10px", borderRadius: 8, border: "1px solid var(--border)",
                    background: "var(--bg)", color: "var(--fg)", fontSize: 13,
                    minWidth: 140,
                  }}
                  title="Filtrar por equipo"
                >
                  <option value="all">🌎 Todos los equipos</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 4px", flexWrap: "wrap" }}>
                <div className="tiny muted" style={{ flex: 1, minWidth: 120 }}>
                  {filteredStickerIds.length} resultado{filteredStickerIds.length === 1 ? "" : "s"}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setPickerSelected((prev) => {
                      const next = new Set(prev);
                      for (const id of filteredStickerIds) next.add(id);
                      return next;
                    });
                  }}
                  style={{ fontSize: 11 }}
                  title="Marca todas las láminas que aparecen ahora"
                >
                  Marcar todas
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const visible = new Set(filteredStickerIds);
                    setPickerSelected((prev) => {
                      const next = new Set(prev);
                      for (const id of visible) next.delete(id);
                      return next;
                    });
                  }}
                  style={{ fontSize: 11 }}
                  title="Quita la marca a las láminas que aparecen ahora"
                >
                  Limpiar
                </button>
              </div>
              <div style={{ overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 6, flex: 1 }}>
                {filteredStickerIds.length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", color: "var(--fg-muted)" }}>
                    {pickerFilter === "smart"
                      ? (pickerSide === "give"
                          ? "No tienes láminas que él necesite. Cambia a 'Mis repetidas' o 'Todo el catálogo'."
                          : "Él no tiene repetidas todavía. Cambia a 'Mis faltantes' o 'Todo el catálogo'.")
                      : pickerFilter === "my-dups"
                      ? "No tienes láminas repetidas todavía. Marca repetidas en tu álbum (×2 o más)."
                      : "Sin resultados"}
                  </div>
                ) : filteredStickerIds.length === 0 && search.trim() ? (
                  <SearchEmptyState
                    search={search}
                    pickerFilter={pickerFilter}
                    pickerSide={pickerSide}
                    stickers={stickers}
                    teamById={teamById}
                    myDupIds={myDupIds}
                    myOwnedIds={myOwnedIds}
                    peerDups={peerDups}
                    peerMissing={peerMissing}
                    onJump={(f) => setPickerFilter(f)}
                  />
                ) : filteredStickerIds.map((id) => {
                  const s = stickerById(id);
                  if (!s) return null;
                  const t = teamById(s.team_id);
                  const myCount = ownership[id] ?? 0;
                  const peerHasDup = peerDups.has(id);
                  const peerNeeds = peerMissing.has(id);
                  const isPicked = pickerSelected.has(id);
                  return (
                    <button
                      type="button"
                      key={id}
                      onClick={() => togglePick(id)}
                      className="propose-pick-item"
                      style={{
                        outline: isPicked ? "2px solid var(--primary)" : undefined,
                        background: isPicked ? "var(--primary-soft)" : undefined,
                      }}
                    >
                      <span style={{
                        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                        border: "2px solid " + (isPicked ? "var(--primary)" : "var(--border-strong)"),
                        background: isPicked ? "var(--primary)" : "transparent",
                        display: "grid", placeItems: "center", color: "white",
                      }}>
                        {isPicked && <Icon name="check" size={11} />}
                      </span>
                      <span style={{ width: 4, height: 28, background: t?.color || "#999", borderRadius: 2, flexShrink: 0 }} />
                      <b style={{ minWidth: 60 }}>{s.num}</b>
                      <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                      {pickerSide === "give" ? (
                        <>
                          {myCount >= 2 && <span className="chip primary" style={{ fontSize: 9 }}>🔥×{myCount}</span>}
                          {myCount === 1 && <span className="chip" style={{ fontSize: 9, background: "var(--bg-subtle)" }}>tienes 1</span>}
                          {myCount === 0 && <span className="chip" style={{ fontSize: 9, background: "var(--bg-subtle)", color: "var(--fg-muted)" }}>no tienes</span>}
                          {peerNeeds && <span className="chip amber" style={{ fontSize: 9 }}>le falta</span>}
                        </>
                      ) : (
                        <>
                          {peerHasDup && <span className="chip amber" style={{ fontSize: 9 }}>tiene repetida</span>}
                          {myCount === 0 && <span className="chip magenta" style={{ fontSize: 9 }}>te falta</span>}
                          {myCount >= 1 && <span className="chip" style={{ fontSize: 9, background: "var(--bg-subtle)", color: "var(--fg-muted)" }}>ya la tienes</span>}
                        </>
                      )}
                      {s.is_legendary && <span className="chip amber" style={{ fontSize: 9, padding: "1px 5px" }}>LEG</span>}
                    </button>
                  );
                })}
              </div>

              {/* Footer del picker — multi-selección. Muestra cuántas marcaron
                  y permite confirmar todas de una vez sin cerrar entre cada una. */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
                <div className="tiny muted" style={{ flex: 1 }}>
                  {pickerSelected.size === 0
                    ? "Tocá las láminas que querés agregar — podés marcar varias a la vez."
                    : `${pickerSelected.size} seleccionada${pickerSelected.size === 1 ? "" : "s"}`}
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPickerSide(null)}>
                  Cancelar
                </button>
                <button type="button" className="btn btn-primary btn-sm" onClick={commitPickerSelection}>
                  <Icon name="check" size={12} /> Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface SidePanelProps {
  title: string;
  accent: string;
  items: Selected[];
  onAdd: () => void;
  onRemove: (id: number) => void;
  onQtyChange: (id: number, q: number) => void;
}

/** Cuando el search no devuelve resultados en el filtro actual, sugerir
 *  filtros alternativos donde SÍ aparece la lámina buscada. Caso típico:
 *  user busca 'FWC-2' que tiene en duplicado, pero el filtro 'smart' la
 *  esconde porque el peer ya la tiene. */
function SearchEmptyState({
  search, pickerFilter, pickerSide, stickers, teamById,
  myDupIds, myOwnedIds, peerDups, peerMissing, onJump,
}: {
  search: string;
  pickerFilter: PickerFilter;
  pickerSide: "give" | "receive";
  stickers: ReturnType<typeof useCatalog>["stickers"];
  teamById: ReturnType<typeof useCatalog>["teamById"];
  myDupIds: Set<number>;
  myOwnedIds: Set<number>;
  peerDups: Set<number>;
  peerMissing: Set<number>;
  onJump: (f: PickerFilter) => void;
}) {
  const q = search.toLowerCase().trim();

  // Aplica el filtro de búsqueda + un filtro de tab dado, y devuelve count.
  const countIn = (f: PickerFilter): number => {
    let pool = stickers;
    if (f === "smart" && pickerSide === "give") {
      pool = pool.filter((s) => peerMissing.has(s.id) && myDupIds.has(s.id));
    } else if (f === "smart" && pickerSide === "receive") {
      pool = pool.filter((s) => peerDups.has(s.id));
    } else if (f === "my-dups") {
      pool = pool.filter((s) => myDupIds.has(s.id));
    } else if (f === "my-missing") {
      pool = pool.filter((s) => !myOwnedIds.has(s.id));
    }
    return pool.filter((s) => {
      const t = teamById(s.team_id);
      return `${s.name} ${s.num} ${t?.code ?? ""} ${t?.name ?? ""}`.toLowerCase().includes(q);
    }).length;
  };

  const alternatives: { filter: PickerFilter; label: string; count: number }[] = [];
  const tryAdd = (f: PickerFilter, label: string) => {
    if (f === pickerFilter) return;
    const n = countIn(f);
    if (n > 0) alternatives.push({ filter: f, label, count: n });
  };

  if (pickerSide === "give") {
    tryAdd("smart", "🎯 Lo que él necesita");
    tryAdd("my-dups", "🔥 Mis repetidas");
  } else {
    tryAdd("smart", "🔥 Sus repetidas");
    tryAdd("my-missing", "❤️ Mis faltantes");
  }
  tryAdd("all", "Todo el catálogo");

  return (
    <div style={{ padding: 16, textAlign: "center", color: "var(--fg-muted)", fontSize: 13 }}>
      <div style={{ marginBottom: 8 }}>
        Sin resultados para "<b>{search}</b>" en este filtro.
      </div>
      {alternatives.length > 0 ? (
        <>
          <div style={{ marginBottom: 8 }}>Probá en:</div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
            {alternatives.map((a) => (
              <button
                key={a.filter}
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => onJump(a.filter)}
                style={{ fontSize: 12 }}
              >
                {a.label} ({a.count})
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="tiny">La lámina no existe en el catálogo o no se encuentra con esa búsqueda.</div>
      )}
    </div>
  );
}


/** Score para ordenar el picker. Mayor = arriba.
 *
 * Filosofía:
 * - GIVE: lo mejor para ofrecer son MIS REPETIDAS que ÉL NECESITA.
 *   - 100: él la necesita Y yo la tengo en duplicado (count >= 2)
 *   - 50: él la necesita Y yo la tengo (count == 1, tendría que pegarla doble)
 *   - 10: yo tengo dup (pero no la necesita)
 *   - 1: yo la tengo simple
 *   - 0: no tengo
 * - RECEIVE: lo mejor para pedirle son MIS FALTANTES que ÉL TIENE en repetida.
 *   - 100: yo la necesito (count == 0) Y él la tiene como dup
 *   - 50: yo la necesito Y él la tiene simple (raro pero posible si filtro 'all')
 *   - 10: él la tiene como dup pero yo ya la tengo
 *   - 1: él la tiene simple
 */
function matchScore(
  id: number,
  side: "give" | "receive" | null,
  peerMissing: Set<number>,
  peerDups: Set<number>,
  myOwnedIds: Set<number>,
  myDupIds: Set<number>,
  ownership: Record<number, number>,
): number {
  const myCount = ownership[id] || 0;
  if (side === "give") {
    if (peerMissing.has(id) && myDupIds.has(id)) return 100 + Math.min(myCount, 99);
    if (peerMissing.has(id) && myOwnedIds.has(id)) return 50;
    if (myDupIds.has(id)) return 10 + Math.min(myCount, 9);
    if (myOwnedIds.has(id)) return 1;
    return 0;
  }
  // receive
  const iNeed = !myOwnedIds.has(id);
  if (iNeed && peerDups.has(id)) return 100;
  if (iNeed) return 50;
  if (peerDups.has(id)) return 10;
  return 1;
}


function SidePanel({ title, accent, items, onAdd, onRemove, onQtyChange }: SidePanelProps) {
  const { stickerById, teamById } = useCatalog();
  return (
    <div className="propose-side" style={{ borderColor: accent }}>
      <div className="propose-side-title" style={{ color: accent }}>
        {title} {items.length > 0 && <span style={{ opacity: 0.7 }}>· {items.length}</span>}
      </div>
      <div className="propose-side-list">
        {items.length === 0 && (
          <div className="propose-side-empty">Sin láminas aún · click en "+" para agregar</div>
        )}
        {items.map((s) => {
          const sticker = stickerById(s.sticker_id);
          const team = sticker ? teamById(sticker.team_id) : null;
          return (
            <div key={s.sticker_id} className="propose-item">
              <div style={{ width: 4, height: 30, background: team?.color || "#999", borderRadius: 2, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 12 }}>{sticker?.num}</div>
                <div style={{ fontSize: 11, color: "var(--fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {sticker?.name}
                </div>
              </div>
              <div className="propose-qty">
                <button type="button" onClick={() => onQtyChange(s.sticker_id, s.quantity - 1)}>−</button>
                <span>{s.quantity}</span>
                <button type="button" onClick={() => onQtyChange(s.sticker_id, s.quantity + 1)}>+</button>
              </div>
              <button type="button" className="icon-btn" onClick={() => onRemove(s.sticker_id)} aria-label="Quitar">
                <Icon name="x" size={12} />
              </button>
            </div>
          );
        })}
      </div>
      <button type="button" className="btn btn-secondary btn-sm propose-add-btn" onClick={onAdd}>
        <Icon name="plus" size={14} /> Agregar lámina
      </button>
    </div>
  );
}
